/** One pre/post check item (screens §A1): fact-sheet-led stem, the answer control for its kind, and the confidence scale. */
import type { AssessmentItemView } from "@bsim/contracts";
import { GxSegmented } from "../theme/Segmented";
import { CONFIDENCE_LEVELS, CONFIDENCE_PROMPT, unitPrompt, unitSuffix, needsConfidence, CONFIDENCE_MISSING_LINE, type Draft } from "./answers";
import { RovingGroup } from "../theme/roving";
import { GxLimitedText } from "../theme/GxLimitedText";

const NOT_SCORED = "Not scored. We use it to discuss.";

function Confidence({ value, onChange }: { readonly value: number | null; readonly onChange: (v: number) => void }) {
  return (
    <div className="gx-ci-confidence">
      <div className="gx-ci-confidence-q">{CONFIDENCE_PROMPT}</div>
      <GxSegmented label={CONFIDENCE_PROMPT} options={CONFIDENCE_LEVELS} value={value} onChange={onChange} testId="check.confidence" />
    </div>
  );
}

export function ItemView({ item, draft, onChange, onCommit }: {
  readonly item: AssessmentItemView; readonly draft: Draft; readonly onChange: (next: Draft) => void; readonly onCommit: () => void;
}) {
  return (
    <div className="gx-ci-item" data-testid={`check.item.${item.itemId}`}>
      <p className="gx-ci-item-stem">{item.stem}</p>
      {item.kind === "scenario_mcq" ? (
        <RovingGroup className="gx-ci-options" role="radiogroup" aria-label="Answer">
          {item.options.map((o) => (
            <button key={o.id} type="button" role="radio" aria-checked={draft.optionId === o.id} className="gx-radiocard gx-ci-option" data-testid={`check.option.${o.id}`} onClick={() => onChange({ ...draft, optionId: o.id })}>
              <span className="gx-radiocard-mark" aria-hidden="true">{o.id.toUpperCase()}</span>
              <span>{o.text}</span>
            </button>
          ))}
        </RovingGroup>
      ) : null}
      {item.kind === "scenario_numeric" ? (
        <>
        <div className="gx-ci-unit gx-small gx-muted" data-testid="check.unit">{unitPrompt(item.unit)}</div>
        <label className="gx-ci-field">
          <span className="gx-sr">Your answer in {unitSuffix(item.unit)}</span>
          <input className="gx-ci-field-input gx-num" inputMode="decimal" autoComplete="off" placeholder="0" value={draft.entry} data-testid="check.numeric" onChange={(e) => onChange({ ...draft, entry: e.target.value })} />
          <span className="gx-ci-field-unit">{unitSuffix(item.unit)}</span>
        </label>
        </>
      ) : null}
      {item.kind === "judgment_case" ? (
        <div className="gx-ci-judgment">
          <GxLimitedText id={`check-text-${item.itemId}`} testId="check.limit" fieldTestId="check.text" label="Your answer" labelHidden max={item.maxChars} rows={7} placeholder="Write in your own words." value={draft.text}
            onChange={(text) => onChange({ ...draft, text })} onBlur={onCommit} />
          <div className="gx-ci-judgment-foot"><span className="gx-faint">{NOT_SCORED}</span></div>
        </div>
      ) : null}
      {item.kind === "judgment_case" ? null : <Confidence value={draft.confidence} onChange={(confidence) => onChange({ ...draft, confidence })} />}
      {needsConfidence(item, draft) ? <p className="gx-small gx-muted" role="status" data-testid="check.confidence.missing">{CONFIDENCE_MISSING_LINE}</p> : null}
    </div>
  );
}
